/**
 * Canvas manipulation utilities for QR code generation
 * Handles circular border creation, dot patterns, and canvas sizing
 */

export interface CircularBorderConfig {
  dotRadius: number;
  dotColor: string;
  borderWidth: number;
  dotDensity: number;
  seed?: number;
}

export const DEFAULT_CIRCULAR_BORDER_CONFIG: CircularBorderConfig = {
  dotRadius: 14,
  dotColor: '#1a1a4d',
  borderWidth: 350,
  dotDensity: 0.65,
  seed: 12345
};

/**
 * Desired final canvas size for the QR code with circular border
 */
export const DESIRED_CANVAS_SIZE = 2168;

/**
 * Calculates the QR code size needed to achieve a target canvas size
 * Based on: canvasSize = qrSize + gap*2 + borderWidth*2 + strokeMargin*2
 */
export const calculateQrSizeFromCanvasSize = (
  targetCanvasSize: number,
  config: CircularBorderConfig = DEFAULT_CIRCULAR_BORDER_CONFIG
): number => {
  const { dotRadius, borderWidth } = config;
  const gap = dotRadius * 2;
  const strokeMargin = 10;
  return targetCanvasSize - gap * 2 - borderWidth * 2 - strokeMargin * 2;
};

/**
 * Seeded pseudo-random number generator for consistent dot placement
 * Uses Linear Congruential Generator (LCG) algorithm
 */
export const seededRandom = (seed: number) => {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
};

/**
 * Adds a circular border with decorative dot pattern around the QR code
 * Creates a new canvas with the QR code centered and surrounded by dots
 */
export const addCircularBorderWithDots = (
  qrCanvas: HTMLCanvasElement,
  config: CircularBorderConfig = DEFAULT_CIRCULAR_BORDER_CONFIG
): HTMLCanvasElement => {
  const { dotRadius, dotColor, borderWidth, dotDensity, seed } = config;

  const qrSize = qrCanvas.width;
  const gap = dotRadius * 2;
  const strokeMargin = 10;
  const newSize = qrSize + gap * 2 + borderWidth * 2 + strokeMargin * 2;

  const canvas = document.createElement('canvas');
  canvas.width = newSize;
  canvas.height = newSize;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return qrCanvas;
  }

  const centerX = newSize / 2;
  const centerY = newSize / 2;
  const outerRadius = newSize / 2 - strokeMargin;

  // Clip to circular region
  ctx.save();
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
  ctx.clip();

  // Fill white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, newSize, newSize);

  // Draw decorative dots using seeded random for consistency
  ctx.fillStyle = dotColor;
  const random = seededRandom(seed ?? 12345);

  const gridSpacing = dotRadius * 3;
  const qrStart = (newSize - qrSize) / 2;
  const qrEnd = qrStart + qrSize;

  for (let x = gridSpacing / 2; x < newSize; x += gridSpacing) {
    for (let y = gridSpacing / 2; y < newSize; y += gridSpacing) {
      const insideQrArea =
        x >= qrStart - gap && x <= qrEnd + gap && y >= qrStart - gap && y <= qrEnd + gap;

      const dx = x - centerX;
      const dy = y - centerY;
      const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);
      const borderRingSpace = 100;
      const insideCircle = distanceFromCenter <= outerRadius - dotRadius * 2 - borderRingSpace;

      if (!insideQrArea && insideCircle) {
        if (random() < dotDensity) {
          ctx.beginPath();
          ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }

  // Draw QR code in center
  const qrX = (newSize - qrCanvas.width) / 2;
  const qrY = (newSize - qrCanvas.height) / 2;
  ctx.drawImage(qrCanvas, qrX, qrY);

  ctx.restore();

  // Draw outer border rings
  ctx.strokeStyle = '#1a1a4d';
  ctx.lineWidth = 50;
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius - 45, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 15;
  ctx.beginPath();
  ctx.arc(centerX, centerY, outerRadius - 15, 0, Math.PI * 2);
  ctx.stroke();

  return canvas;
};
